import React, { useEffect, useLayoutEffect, useRef } from "react";

import { useClosestScrollingContainerOrNull } from "../../components/_scrolling-container";

import type { UseConversationLifecycleResult } from "./_conversation-lifecycle-types";
import { RefreshButton } from "./_refresh-button";
import { SessionErrorDisplay } from "./_session-error-display";
import type { ComponentViewMode } from "./_use-navigate-to-component";
import { MessageThread } from "./message-thread";

const STICKY_BOTTOM_THRESHOLD_PX = 48;
const SCROLL_UP_EPSILON_PX = 1;

export type LifecycleResultLike = Pick<
	UseConversationLifecycleResult,
	"phase" | "error" | "optimisticUserMessage" | "isFetching" | "retry" | "refetch" | "messages" | "currentMessage"
> & {
	submitFollowUp: UseConversationLifecycleResult["sendMessage"];
};

export interface SessionContentLayoutProps {
	lifecycle: LifecycleResultLike;
	autoScroll?: boolean;
	onRetrySend?: () => void;
	onResend?: (text: string) => void;
	onNavigateToComponent?: (identifier: string, mode?: ComponentViewMode) => void;
}

export const SessionContentLayout: React.FC<SessionContentLayoutProps> = ({
	lifecycle,
	autoScroll = true,
	onRetrySend,
	onResend,
	onNavigateToComponent,
}) => {
	const scrollEndRef = useRef<HTMLDivElement>(null);
	const scrollingContainer = useClosestScrollingContainerOrNull();
	const scrollElement = scrollingContainer?.element ?? null;
	const stickToBottomRef = useRef(true);
	const lastScrollTopRef = useRef<null | number>(null);
	const { currentMessage, messages, optimisticUserMessage, phase, refetch, retry, submitFollowUp } = lifecycle;

	useEffect(() => {
		if (scrollElement == null) {
			return;
		}

		lastScrollTopRef.current = scrollElement.scrollTop;

		const updateStickiness = () => {
			const previousScrollTop = lastScrollTopRef.current ?? scrollElement.scrollTop;
			const currentScrollTop = scrollElement.scrollTop;
			const distanceFromBottom = scrollElement.scrollHeight - currentScrollTop - scrollElement.clientHeight;
			const isNearBottom = distanceFromBottom <= STICKY_BOTTOM_THRESHOLD_PX;
			const scrolledUp = currentScrollTop < previousScrollTop - SCROLL_UP_EPSILON_PX;

			if (scrolledUp) {
				stickToBottomRef.current = false;
			} else if (isNearBottom) {
				stickToBottomRef.current = true;
			}

			lastScrollTopRef.current = currentScrollTop;
		};

		scrollElement.addEventListener("scroll", updateStickiness, { passive: true });

		return () => {
			scrollElement.removeEventListener("scroll", updateStickiness);
		};
	}, [scrollElement]);

	// Auto-scroll to bottom as data changes, but only while the user is still
	// in follow mode. Scrolling up disables follow immediately; scrolling back
	// near the bottom re-enables it.
	useLayoutEffect(() => {
		const isLiveStreaming = phase === "polling" && currentMessage != null;
		if (autoScroll && (stickToBottomRef.current || !isLiveStreaming)) {
			scrollEndRef.current?.scrollIntoView({ behavior: isLiveStreaming ? "auto" : "smooth" });
		}
	}, [autoScroll, currentMessage, phase]);

	return (
		<div className="flex flex-col gap-4">
			<MessageThread
				currentMessage={currentMessage}
				messages={messages}
				onNavigateToComponent={onNavigateToComponent}
				onResend={onResend}
				onSuggestionClick={submitFollowUp}
				optimisticUserMessage={optimisticUserMessage}
			/>

			{lifecycle.phase === "create_failed" && (
				<SessionErrorDisplay message={`Failed to start session: ${lifecycle.error}`} onRetry={retry} />
			)}

			{onRetrySend && lifecycle.phase === "send_failed" && (
				<SessionErrorDisplay message={`Failed to send message: ${lifecycle.error}`} onRetry={onRetrySend} />
			)}

			{lifecycle.phase === "error" && (
				<SessionErrorDisplay message={`Session failed: ${lifecycle.error}`} onRetry={onRetrySend} />
			)}

			{lifecycle.phase === "cancelled" && (
				<SessionErrorDisplay
					message="Stopped current response. You can continue the conversation below."
					variant="system"
				/>
			)}

			{(lifecycle.phase === "polling" || lifecycle.phase === "error") && (
				<RefreshButton isFetching={lifecycle.isFetching} onRefresh={refetch} />
			)}

			<div ref={scrollEndRef} />
		</div>
	);
};
